import { useState, useEffect, useMemo } from "react";
import { listUserQuotes, updateQuote, deleteQuote, pinQuote, toggleQuoteLike, QUOTE_MAX } from "../api/quotes";
import firstSentence from "../utils/firstSentence";
import ExportCsvButton from "../components/ExportCsvButton";
import { quotesToCsv, quotesCsvFileName } from "../utils/quotesCsv";

/**
 * 한 사람이 모은 문장.
 *
 * 내 것이면 걸어둘 문장을 여기서 고른다. 예전에는 프로필 수정에서
 * 매번 새로 써야 했는데, 이제는 모아둔 것 중에 고르는 일이 된다.
 */
export default function UserQuotes({ user, isMe, currentUser, onBookClick, focusId, clearFocus, onFocusMissed }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinned, setPinned] = useState(user.pinnedQuoteId ?? null);
  const [query, setQuery] = useState("");
  /** 처음에 보여줄 개수. 넘치면 '더 보기' 로 접는다. */
  const PAGE = 5;
  const [shown, setShown] = useState(PAGE);
  /** 펼쳐 둔 문장. 목록에서는 첫 문장만 보인다. */
  const [opened, setOpened] = useState(() => new Set());
  /** 고치는 중인 문장 id. 책 화면의 문장 목록과 같은 방식이다. */
  const [editing, setEditing] = useState(null);
  const [eText, setEText] = useState("");
  const [ePage, setEPage] = useState("");
  const [ePublic, setEPublic] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    listUserQuotes(user.id)
      .then((q) => { if (alive) setQuotes(q); })
      .catch((e) => console.error("문장 로드 실패:", e.message))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user.id]);

  const pick = async (id) => {
    const next = pinned === id ? null : id;
    setPinned(next);
    try {
      await pinQuote(currentUser.uid, next);
    } catch (e) {
      setPinned(pinned); // 되돌린다
      console.error("걸어두기 실패:", e.message);
    }
  };

  const startEdit = (q) => {
    setEditing(q.id);
    setEText(q.text);
    setEPage(q.page ? String(q.page) : "");
    setEPublic(q.isPublic);
  };

  /* 목록을 다시 불러오지 않고 그 줄만 갈아끼운다. 여기서 다시 불러오면
     펼쳐둔 것과 '더 보기' 로 늘린 자리가 모두 처음으로 돌아간다. */
  const saveEdit = async (id) => {
    const text = eText.trim().slice(0, QUOTE_MAX);
    if (!text) return;
    const page = ePage ? Number(ePage) : null;
    setSaving(true);
    try {
      await updateQuote(id, { text, page, isPublic: ePublic });
      setQuotes((prev) => prev.map((x) => (x.id !== id ? x : { ...x, text, page, isPublic: ePublic })));
      setEditing(null);
    } catch (e) {
      console.error("문장 수정 실패:", e.message);
    }
    setSaving(false);
  };

  // 문장만 훑으면 "김애란" 으로 못 찾는다. 책과 저자까지 함께 본다 —
  // 대개 기억나는 건 문장이 아니라 누가 쓴 어느 책이다.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return quotes;
    return quotes.filter((x) =>
      [x.text, x.bookTitle, x.bookAuthor].some((f) => (f || "").toLowerCase().includes(q))
    );
  }, [quotes, query]);

  /* 아래 early return(불러오는 중·빈 목록)보다 앞에 둔다 — 훅 순서가 바뀌면 안 된다. */
  /** 방금 데려온 문장. 잠깐 테두리를 밝힌다. */
  const [flash, setFlash] = useState(null);

  /* 오늘의 문장에서 넘어오면 그 문장까지 데려간다.
     목록은 따로 불러오고 다섯 개씩 끊어 보이므로, 다 불러온 뒤 몇 번째인지
     보고 거기까지 펼친다. 긴 문장은 접혀 있어 함께 연다.
     목록에 없으면(그새 비공개로 돌렸다든가) 탭 줄로만 데려간다. */
  useEffect(() => {
    if (!focusId || loading) return;
    const i = filtered.findIndex((x) => String(x.id) === String(focusId));
    clearFocus?.();
    if (i < 0) { onFocusMissed?.(); return; }
    if (i >= shown) setShown(i + 1);
    setOpened((prev) => new Set(prev).add(filtered[i].id));
    setFlash(filtered[i].id);
  }, [focusId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  /* 스크롤·밝힘은 flash 에 묶는다. focusId 에 묶으면 clearFocus() 가 곧바로
     이펙트를 다시 돌려 예약해 둔 일을 취소한다. */
  useEffect(() => {
    if (!flash) return;
    const place = () => {
      const el = document.querySelector(`[data-quote-id="${CSS.escape(String(flash))}"]`);
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const h = el.offsetHeight;
      const y = h < window.innerHeight * 0.7
        ? window.scrollY + top - (window.innerHeight - h) / 2
        : window.scrollY + top - 90;
      window.scrollTo(0, Math.max(0, y));
    };
    const raf = requestAnimationFrame(place);
    const again = setTimeout(place, 320);
    const off = setTimeout(() => setFlash(null), 2400);
    return () => { cancelAnimationFrame(raf); clearTimeout(again); clearTimeout(off); };
  }, [flash]);

  if (loading) return <div className="uq-empty">불러오는 중…</div>;

  if (quotes.length === 0) {
    return (
      <div className="uq-empty">
        {isMe ? (
          <>
            아직 옮긴 문장이 없어요.
            <div className="uq-empty-sub">책을 열고 기억에 남은 문장을 옮겨보세요.</div>
          </>
        ) : (
          "아직 공개된 문장이 없어요."
        )}
      </div>
    );
  }

  const visible = filtered.slice(0, shown);
  const rest = filtered.length - visible.length;

  return (
    <div className="uq">
      {/* 내 문장만 받을 수 있다. 남의 문장을 파일로 뽑아 돌리는 건 다른 이야기다.
          검색으로 걸러낸 목록이 아니라 모아둔 문장 전부를 담는다 — 받는 사람이
          기대하는 것은 '내 문장 전부' 다. */}
      {isMe && (
        <ExportCsvButton
          build={() => quotesToCsv(quotes)}
          fileName={quotesCsvFileName()}
          count={quotes.length}
          label="문장 내보내기"
          event="quotes_csv_export"
        />
      )}

      {/* 문장이 쌓이면 스크롤로는 못 찾는다 */}
      {quotes.length > PAGE && (
        <div className="uq-search">
          <span className="uq-search-icon" aria-hidden="true">🔍</span>
          <input
            className="uq-search-input"
            placeholder="문장 · 책 · 저자 검색"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShown(PAGE); }}
          />
          {query && (
            <button className="uq-search-clear" onClick={() => setQuery("")} aria-label="지우기">✕</button>
          )}
        </div>
      )}

      {/* 0개일 때는 아래 안내가 이미 말하므로 세지 않는다 */}
      {query && filtered.length > 0 && (
        <div className="uq-found">{filtered.length}개 찾음</div>
      )}

      {isMe && !query && <div className="uq-hint">눌러서 행성에 걸어둘 문장을 고를 수 있어요</div>}

      {filtered.length === 0 && query && (
        <div className="uq-empty">찾는 문장이 없어요</div>
      )}

      {visible.map((q) => (
        /* 고치는 중에는 줄을 눌러도 '걸어두기' 가 되지 않는다. 입력칸을
           짚을 때마다 행성에 걸렸다 풀렸다 하면 글을 쓸 수 없다. */
        <div
          key={q.id}
          data-quote-id={q.id}
          className={`uq-item${pinned === q.id ? " pinned" : ""}${isMe && editing !== q.id ? " pickable" : ""}${flash === q.id ? " uq-focus" : ""}`}
          onClick={() => isMe && editing !== q.id && pick(q.id)}
        >
          {editing === q.id ? (
            /* 책 화면의 문장 폼과 같은 옷을 입힌다(bq-*). 좋아요 버튼도
               이미 그 클래스를 같이 쓰고 있다. */
            <div className="bq-form uq-editform" onClick={(e) => e.stopPropagation()}>
              <textarea
                className="bq-input"
                value={eText}
                maxLength={QUOTE_MAX}
                rows={4}
                autoFocus
                onChange={(e) => setEText(e.target.value)}
              />
              <div className="bq-row">
                <input
                  className="bq-page"
                  type="number"
                  inputMode="numeric"
                  placeholder="쪽수"
                  value={ePage}
                  min="1"
                  onChange={(e) => setEPage(e.target.value)}
                />
                <span className={eText.length >= QUOTE_MAX ? "bq-count full" : "bq-count"}>
                  {eText.length} / {QUOTE_MAX}
                </span>
              </div>
              <label className="bq-check">
                <input type="checkbox" checked={ePublic} onChange={(e) => setEPublic(e.target.checked)} />
                <span>다른 독자에게도 보이기</span>
              </label>
              <div className="bq-btns">
                <button className="bq-save" onClick={() => saveEdit(q.id)} disabled={!eText.trim() || saving}>
                  {saving ? "저장 중…" : "수정 완료"}
                </button>
                <button className="bq-cancel" onClick={() => setEditing(null)}>취소</button>
              </div>
            </div>
          ) : (
          <>
          <span className="uq-mark" aria-hidden="true">“</span>
          <div className="uq-body">
            {/* 긴 인용을 통째로 펼쳐두면 여섯 줄짜리 하나가 화면을 덮어
                다음 문장이 눈에 들어오지 않는다. 첫 문장만 보인다.
                항목을 누르면 걸어두기가 되므로 펼치기는 따로 둔다. */}
            {(() => {
              const { head, rest } = firstSentence(q.text);
              const open = opened.has(q.id);
              if (!rest) return <div className="uq-text">{q.text}</div>;
              return (
                <div className="uq-text">
                  {open ? q.text : head}
                  <button
                    className="uq-expand"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpened((prev) => {
                        const next = new Set(prev);
                        if (next.has(q.id)) next.delete(q.id);
                        else next.add(q.id);
                        return next;
                      });
                    }}
                    aria-expanded={open}
                  >{open ? "접기" : "… 더 보기"}</button>
                </div>
              );
            })()}
            <div className="uq-meta">
              <button
                className="uq-book"
                onClick={(e) => { e.stopPropagation(); onBookClick?.(q.bookId); }}
              >
                『{q.bookTitle}』{q.bookAuthor ? ` ${q.bookAuthor}` : ""}
              </button>
              {q.page && <span>p.{q.page}</span>}
              {!q.isPublic && <span className="uq-priv">비공개</span>}
              {/* 나만 보는 문장에는 좋아요를 두지 않는다. */}
              {q.isPublic && (() => {
                const me = currentUser?.uid;
                const liked = !!me && (q.likes ?? []).includes(me);
                return (
                  <button
                    className={`bq-like${liked ? " on" : ""}`}
                    aria-pressed={liked}
                    aria-label={liked ? "좋아요 취소" : "좋아요"}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!me) return;
                      setQuotes((prev) => prev.map((x) => x.id !== q.id ? x : {
                        ...x,
                        likes: liked ? x.likes.filter((v) => v !== me) : [...(x.likes ?? []), me],
                      }));
                      try {
                        await toggleQuoteLike(q.id, me, liked);
                      } catch (err) {
                        console.error("좋아요 실패:", err.message);
                      }
                    }}
                  >
                    {liked ? "♥" : "♡"}
                    {(q.likes?.length ?? 0) > 0 && <span className="bq-like-n">{q.likes.length}</span>}
                  </button>
                );
              })()}
              {isMe && (
                /* 둘을 한 덩어리로 묶는다. 따로 두면 좁은 폭에서 '지우기' 만
                   다음 줄로 떨어진다. */
                <span className="uq-acts">
                  <button
                    className="uq-edit"
                    onClick={(e) => { e.stopPropagation(); startEdit(q); }}
                  >고치기</button>
                  <button
                    className="uq-del"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await deleteQuote(q.id);
                      setQuotes((prev) => prev.filter((x) => x.id !== q.id));
                    }}
                  >지우기</button>
                </span>
              )}
            </div>
          </div>
          </>
          )}
          {pinned === q.id && <span className="uq-pin" title="행성에 걸어둔 문장">✓</span>}
        </div>
      ))}

      {/* 펼치기만 있으면 한 번 늘린 목록을 되돌릴 수 없다.
          늘린 뒤에는 접기도 함께 둔다. */}
      {(rest > 0 || shown > PAGE) && (
        <div className="uq-page">
          {rest > 0 && (
            <button className="uq-more" onClick={() => setShown((n) => n + PAGE)}>
              ▾ {rest}개 더 보기
            </button>
          )}
          {shown > PAGE && (
            <button className="uq-more uq-less" onClick={() => setShown(PAGE)}>
              ▴ 접기
            </button>
          )}
        </div>
      )}
    </div>
  );
}
